import { Button, Divider, Form, Input, InputNumber, Typography } from '@pankod/refine-antd';
import { useCan } from '@pankod/refine-core';
import { ISale } from 'interfaces';
import { IReturnProduct } from 'reducers/product-return-reducer';

type EditReturnProductProps = {
  editProduct: IReturnProduct;
  options: ISale['salesProductsCopy'];
  onSubmit: (editedProduct: IReturnProduct) => void;
};

export const EditReturnProduct: React.FC<EditReturnProductProps> = (props) => {
  const [form] = Form.useForm();
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  const onReturnQuantityChange = (value: number | null) => {
    const unitPrice = form.getFieldValue('unitPrice') || 0;
    const subTotal = value ? value * unitPrice : 0;
    form.setFieldsValue({ subTotal });
  };

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = props.editProduct.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const customFields = props.editProduct.customFields ?? [];

  const attributeElements = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFieldElements = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  interface FormValues {
    salesProduct: string;
    product: string;
    unitPrice: number;
    returnQuantity: number;
    subTotal: number;
    lossAmount: number;
  }

  const onFinish = (values: FormValues) => {
    const saleProduct = props.options.find(
      (saleProduct) => saleProduct.productVariant.id === props.editProduct.productVariantId
    );
    if (!saleProduct) return;

    const data = {
      ...props.editProduct,
      unitPrice: Number.parseFloat(values.unitPrice.toFixed(2)),
      quantity: values.returnQuantity,
      subTotal: Number.parseFloat(values.subTotal.toFixed(2)),
      lossAmount: Number.parseFloat(values.lossAmount.toFixed(2)),
      salesProduct: values.salesProduct,
    };

    if (data.quantity > saleProduct.quantity || data.quantity === 0) {
      form.setFields([
        {
          name: 'returnQuantity',
          errors: [`Quantity needs to be between 1 and ${saleProduct.quantity}`],
        },
      ]);
      return;
    }

    props.onSubmit(data);
    form.resetFields();
  };

  const variantFormItemValue = `${props.editProduct.sku} - ${props.editProduct.productVariantName} (${props.editProduct.quantity})`;

  return (
    <Form
      layout="vertical"
      initialValues={{
        salesProduct: props.editProduct.salesProduct,
        variant: variantFormItemValue,
        returnQuantity: props.editProduct.quantity,
        unitPrice: props.editProduct.unitPrice,
        subTotal: props.editProduct.unitPrice * props.editProduct.quantity,
        lossAmount: props.editProduct.lossAmount,
      }}
      form={form}
      onFinish={onFinish}
    >
      <Form.Item label="Sales Product ID" name="salesProduct" style={{ display: 'none' }}>
        <Input disabled />
      </Form.Item>
      <Form.Item label="Product" name="variant">
        <Input disabled />
      </Form.Item>

      <Divider />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
          <Typography.Text code>{props.editProduct.productName}</Typography.Text>
        </div>

        {attributeElements}
        {customFieldElements}

        {readPurchasePrice?.can && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography.Text strong>PURCHASE PRICE:</Typography.Text>
            <Typography.Text>{props.editProduct.purchasePrice}</Typography.Text>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Text strong>SELLING PRICE:</Typography.Text>
          <Typography.Text>{props.editProduct.sellingPrice}</Typography.Text>
        </div>
      </div>
      <Divider />

      <Form.Item label="Unit Price" name="unitPrice" style={{ display: 'none' }}>
        <Input disabled />
      </Form.Item>
      <Form.Item label="Return Quantity" name="returnQuantity" rules={[{ required: true }]}>
        <InputNumber style={{ width: '100%' }} onChange={onReturnQuantityChange} />
      </Form.Item>
      <Form.Item label="Value" name="subTotal">
        <InputNumber disabled style={{ width: '100%' }} min={0} />
      </Form.Item>
      <Form.Item label="Loss Amount" name="lossAmount">
        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
      </Form.Item>

      <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};
